<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { NEW_CUSTOM_FIELD_TYPES, buildNewCustomField } from './fieldCatalog';

const emit = defineEmits(['created']);

const { t } = useI18n();

const dialogRef = ref(null);

const blank = () => ({
  scope: 'contact',
  label: '',
  type: 'text',
  optionsText: '',
});
const field = reactive(blank());

const hasOptions = computed(() => ['list', 'radio'].includes(field.type));
const canCreate = computed(() => !!field.label.trim());

const typeOptions = NEW_CUSTOM_FIELD_TYPES.map(type => ({
  value: type,
  label: t(`FORM_BUILDER.EDITOR.TYPES.${type.toUpperCase()}`),
}));

const scopeOptions = computed(() => [
  { value: 'contact', label: t('FORM_BUILDER.EDITOR.GROUPS.CONTACT') },
  { value: 'deal', label: t('FORM_BUILDER.EDITOR.GROUPS.DEAL') },
]);

const open = () => {
  Object.assign(field, blank());
  dialogRef.value?.open();
};

const create = () => {
  if (!canCreate.value) return;
  const options = hasOptions.value
    ? field.optionsText
        .split('\n')
        .map(option => option.trim())
        .filter(Boolean)
    : [];
  const built = buildNewCustomField({
    scope: field.scope,
    label: field.label.trim(),
    type: field.type,
    options,
  });
  emit('created', { ...built, optionsText: field.optionsText });
  dialogRef.value?.close();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('FORM_BUILDER.EDITOR.FIELDS.NEW_CUSTOM_FIELD')"
    :confirm-button-label="t('FORM_BUILDER.EDITOR.FIELDS.ADD_CUSTOM')"
    :disable-confirm-button="!canCreate"
    @confirm="create"
  >
    <div class="flex flex-col gap-3">
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('FORM_BUILDER.EDITOR.FIELDS.SCOPE') }}
        <ComboBox v-model="field.scope" :options="scopeOptions" />
      </label>
      <Input
        v-model="field.label"
        :label="t('FORM_BUILDER.EDITOR.FIELDS.LABEL')"
      />
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('FORM_BUILDER.EDITOR.FIELDS.TYPE') }}
        <ComboBox v-model="field.type" :options="typeOptions" />
      </label>
      <label
        v-if="hasOptions"
        class="flex flex-col gap-1 text-sm text-n-slate-12"
      >
        {{ t('FORM_BUILDER.EDITOR.FIELDS.OPTIONS') }}
        <textarea
          v-model="field.optionsText"
          rows="4"
          class="p-2 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
        />
      </label>
    </div>
  </Dialog>
</template>
